export function photoMemoryText(capture) {
  return [
    capture.explanation?.trim() && `## Your explanation\n${capture.explanation.trim()}`,
    capture.voiceText?.trim() && `## Your spoken explanation\n${capture.voiceText.trim()}`,
    capture.imageDescription?.trim() && `## Photo description (AI-generated)\n${capture.imageDescription.trim()}`,
  ].filter(Boolean).join('\n\n');
}
export function organizationSucceeded(result) {
  return Boolean(result && Number.isInteger(result.operations) && result.operations >= 0
    && Array.isArray(result.results) && result.results.length === result.operations
    && result.results.every(r => r?.ok === true));
}
const terminal = new Set(['completed','failed','canceled','expired']);
const statuses = new Set(['queued','running','retry_wait',...terminal]);
export function photoJobPending(capture) {
  return Boolean(capture.backgroundJobId && !terminal.has(capture.processingStatus));
}
export function photoJobLabel(capture) {
  if (capture.processingStatus === 'completed') return capture.imageSyncError ? 'Organized · original photos on this device' : 'Saved and organized';
  if (capture.processingStatus === 'failed') return capture.cloudSynced ? 'Searchable · processing needs a retry' : 'Uploaded · processing needs a retry';
  if (capture.processingStatus === 'expired') return 'Processing expired · originals kept on this device';
  if (capture.processingStatus === 'canceled') return 'Processing canceled';
  if (capture.processingStatus === 'retry_wait') return 'Uploaded · retrying in the background';
  return ({transcribe:'Uploaded · transcribing',describe:'Uploaded · describing photos',organize:'Searchable · organizing',images:'Organized · syncing original photos'})[capture.processingStage] || 'Uploaded · queued for processing';
}
export function applyPhotoJob(capture, job) {
  if (!job || job.id !== capture.id || !statuses.has(job.status)) throw new Error('Invalid processing status');
  if (job.status === 'completed' && (!organizationSucceeded(job.organization) || !job.organizedAt || !Number.isFinite(Date.parse(job.organizedAt)))) throw new Error('Organization did not finish');
  capture.backgroundJobId = job.id;
  capture.processingStatus = job.status;
  capture.processingStage = job.stage;
  capture.cloudSynced = job.searchable === true;
  capture.photoSyncError = ['failed','expired','canceled'].includes(job.status) ? (job.error || 'Processing needs attention') : null;
  capture.imageSyncError = job.imageSyncError || null;
  if (typeof job.hasImages === 'boolean') capture.hasImages = job.hasImages;
  if (typeof job.voiceText === 'string') capture.voiceText = job.voiceText;
  if (typeof job.imageDescription === 'string') capture.imageDescription = job.imageDescription;
  if (typeof job.text === 'string') {
    capture.text = job.text;
    capture.title = (capture.explanation || capture.voiceText || capture.imageDescription || 'Photo memory').trim().split('\n')[0].replace(/^#+\s*/, '').slice(0,90);
  }
  if (organizationSucceeded(job.organization)) capture.synthesizedAt = job.organizedAt;
}
// The caller saves originals locally first. This only uploads/enqueues or reads
// status; all AI work belongs to the durable server worker. Never fall back to
// browser orchestration after an ambiguous upload response.
export async function syncPhotoMemory(capture, {api,persist,onStep=()=>{},displayName='',retry=false}) {
  if (!api.enabled) return {ok:false};
  try {
    let job;
    if (!capture.backgroundJobId) {
      capture.photoSyncAttempts=(capture.photoSyncAttempts || 0)+1;
      // Freeze metadata before the request so retrying a lost 202 is identical,
      // even if the user's display name or settings change later.
      capture.jobDisplayName ??= displayName;
      await persist(capture);
      onStep('Uploading for background processing… Keep this open until the upload finishes.');
      job=await api.enqueuePhotoMemory(capture,capture.jobDisplayName);
    } else if (retry && capture.processingStatus==='failed') {
      job=await api.retryPhotoMemory(capture.backgroundJobId);
    } else {
      job=await api.getPhotoMemoryJob(capture.backgroundJobId);
    }
    applyPhotoJob(capture,job);
    await persist(capture);
    onStep(photoJobLabel(capture));
    return {ok:job.status==='completed',accepted:true,pending:photoJobPending(capture)};
  } catch(error) {
    capture.photoSyncError=error?.message || 'Unable to check processing';
    await persist(capture);
    return {ok:false,accepted:Boolean(capture.backgroundJobId)};
  }
}
